import './ModelInsights.scss';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { Tooltip, TooltipPayloadEntry } from 'recharts';
import { SaturationDataUnformatted } from '@blisspointmedia/bpm-types/dist/BrandEquity';
import {
  capitalizeWords,
  currencyFormatter,
  MedianOrMean,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { buildEquidistantScale, useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { LineChart } from '@Components/Charts/LineChart/LineChart';
import { X_AXIS_LABEL_HEIGHT } from '@Components/Charts/XAxisLabel';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { getColorMap as colors, Neutral200 } from '@src/utils/colors';
import { formatModelOverviewOutcomeVariable } from '../BrandEquityUtils';
import { MedianLegendLabel } from '../MedianLegendLabel';
import { isQueryVolumeMetric } from '../metricNormalization';
import { EFFECT_DECAY_INFO_COPY, SATURATION_INFO_COPY } from '../modelInsightsCopy';
import { SaturationCurveChart } from '../SaturationCurveChart';
import { DecayDataUnformatted, useSaturationDecayDownloads } from '../useSaturationDecayDownloads';
import { decayDataFormatter, saturationDataFormatterWithGroupedLines } from './BrandImpactUtils';

interface ModelInsightsProps {
  company: string;
  groupByMetric: string;
  saturationDataUnformatted: SaturationDataUnformatted[];
  decayDataUnformatted: DecayDataUnformatted[];
  queryVolumeShortLabel: string;
  centralMeasure: MedianOrMean;
}

const TICK_COUNT = 6;

// Matches R.sortBy(R.compose(R.toLower, R.prop('channel'))): ascending by lowercased
// channel using code-unit comparison (not locale-aware), preserving stable order.
const byChannelNameAsc = (a: { channel: string }, b: { channel: string }): number => {
  const ka = a.channel.toLowerCase();
  const kb = b.channel.toLowerCase();
  if (ka < kb) return -1;
  if (ka > kb) return 1;
  return 0;
};

interface SaturationSpendTooltipProps {
  // Injected by Recharts when this element is passed as the Tooltip `content`.
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  // Supplied at the call site.
  centralMeasure: MedianOrMean;
  saturationData: {
    centralMeasures: Record<string, any>;
  };
  queryVolumeShortLabel: string;
}

// Bespoke saturation-curve tooltip (Brand Impact): channel row + central-measure
// spend + spend + query-volume response. Hoisted to a stable component and passed
// as an element so Recharts clones in `active`/`payload` (mirrors
// SaturationCentralMeasureDot) rather than defining a component during render.
const SaturationSpendTooltip = ({
  active,
  payload,
  centralMeasure,
  saturationData,
  queryVolumeShortLabel
}: SaturationSpendTooltipProps): ReactElement | null => {
  if (!(active && payload && payload.length)) {
    return null;
  }
  const items = payload
    .map(item => ({
      label: String(item.name),
      value: Number(item.value),
      color: item.stroke
    }))
    .sort((a, b) => {
      const ka = -Math.abs(a.value);
      const kb = -Math.abs(b.value);
      if (ka < kb) return -1;
      if (ka > kb) return 1;
      return 0;
    });

  return (
    <div className='sat-chart-tooltip'>
      <div className='item-list'>
        {items.map((item, i) => {
          const { label, value, color } = item;
          if (!value && value !== 0) {
            return null;
          }

          return (
            <div className='item-row' key={i}>
              <div className='item-wrapper data-label'>
                <div />
                <div className='data-label-content'>
                  <div className='line' style={{ backgroundColor: color }} />
                  <div className='name'>{`${label}: `}</div>
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>{centralMeasure} Spend:</div>
                <div className='value'>
                  {currencyFormatter.format(
                    saturationData.centralMeasures[payload[0].payload.name][
                      centralMeasure.toLowerCase()
                    ],
                    0
                  )}
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>Spend:</div>
                <div className='value'>{currencyFormatter.format(payload[0].payload.spend, 0)}</div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>{`${queryVolumeShortLabel}:`}</div>
                <div className='value'>
                  {numberFormatter.format(payload[0].payload.response, 2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ModelInsights = ({
  company,
  groupByMetric,
  saturationDataUnformatted,
  decayDataUnformatted,
  queryVolumeShortLabel,
  centralMeasure
}: ModelInsightsProps): ReactElement => {
  const groupByMetricName = isQueryVolumeMetric(groupByMetric)
    ? queryVolumeShortLabel
    : groupByMetric;
  const [activeGroup, setActiveGroup] = useState(null);

  const saturationDataNew = useMemo(() => {
    if (saturationDataUnformatted.length !== 0) {
      return saturationDataFormatterWithGroupedLines(saturationDataUnformatted, centralMeasure);
    }
    return {} as any;
  }, [centralMeasure, saturationDataUnformatted]);

  const [saturationDataXScale, saturationDataYScale] = useMemo(() => {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    for (const line of saturationDataNew.newD) {
      for (const point of line.data) {
        xMin = Math.max(0, Math.min(xMin, point.spend));
        xMax = Math.max(xMax, point.spend);
        yMin = Math.max(0, Math.min(yMin, point.response));
        yMax = Math.max(yMax, point.response);
      }
    }
    return [
      buildEquidistantScale(xMin, xMax, TICK_COUNT),
      buildEquidistantScale(yMin, yMax, TICK_COUNT)
    ];
  }, [saturationDataNew]);

  const decayDataNew = useMemo(() => {
    if (decayDataUnformatted.length !== 0) {
      return decayDataFormatter(decayDataUnformatted, 'spend_carryover');
    }
    // Cast to preserve the pre-existing `any` shape: callers read `.newD`/`.lines`/
    // `.groupedLines`, which the empty-array fallback would otherwise narrow away.
    return [] as any;
  }, [decayDataUnformatted]);

  const [activeLine, setActiveLine] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + X_AXIS_LABEL_HEIGHT + 5,
    yAxisWidthModifier: x => x + 3
  });

  const colorMap = useMemo(() => {
    if (Object.keys(saturationDataNew).length !== 0) {
      return colors(saturationDataNew.newD);
    }
    return undefined;
  }, [saturationDataNew]);

  const handleLegendClick = useCallback(
    (line, group?) => {
      if (activeLine === line.name) {
        setActiveLine(null);
        setActiveGroup(null);
      } else {
        setActiveLine(line.name);
        setActiveGroup(group.channel);
      }
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    line => {
      setHoveredLegend(hoveredLegend === line.name ? null : line.name);
    },
    [hoveredLegend]
  );

  const tooltip = (
    <Tooltip
      content={
        <SaturationSpendTooltip
          centralMeasure={centralMeasure}
          saturationData={saturationDataNew}
          queryVolumeShortLabel={queryVolumeShortLabel}
        />
      }
      isAnimationActive={false}
    />
  );

  const { excelDownloadSat, pngDownloadSat, excelDownloadDecay, pngDownloadDecay } =
    useSaturationDecayDownloads(
      company,
      saturationDataUnformatted,
      decayDataUnformatted,
      '.brand-impact-model-insights'
    );

  const buildLegendByGroupedArea = useCallback(
    groupedAreas =>
      groupedAreas.map((group, i) => (
        <div className='legend-item' key={`satDecayLegenditem${i}`}>
          <div className='legend-value'>
            <div>
              <div className='legend-header-wrapper'>
                <div
                  className={`legend-header ${
                    activeGroup === group.channel || activeGroup === null ? '' : 'inactive-legend'
                  }`}
                >
                  {group.channel}
                </div>
              </div>
              {group.areas.map((area, j) => {
                const isActiveArea = activeLine === null ? true : activeLine === area.name;
                const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
                const activeColor = isActiveArea ? colorMap[area.name] : Neutral200;
                const hoverColor = isHoveredLegend ? colorMap[area.name] : Neutral200;
                return (
                  <InteractiveDiv
                    key={`satDecayLegendsubHeader${j}`}
                    className='sub-header-wrapper'
                    onClick={() => handleLegendClick(area, group)}
                    onMouseEnter={() => handleLegendEnter(area)}
                    onMouseLeave={() => setHoveredLegend(null)}
                  >
                    <div
                      className='line'
                      style={{
                        backgroundColor: `${activeLine ? activeColor : hoverColor}`
                      }}
                    />
                    <div className='sub-header-text-wrap'>
                      <div className={`sub-header-1 ${isActiveArea ? '' : 'inactive-legend'}`}>
                        {area.grouping.platform}
                      </div>
                      {area.grouping.platform &&
                        (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                          <div className='sub-header-separator'> – </div>
                        )}
                      <div className={`sub-header-2 ${isActiveArea ? '' : 'inactive-legend'}`}>{`${
                        area.grouping.tactic ? area.grouping.tactic : ''
                      }${area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''}${
                        area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''
                      }`}</div>
                    </div>
                  </InteractiveDiv>
                );
              })}
            </div>
          </div>
        </div>
      )),
    [activeLine, activeGroup, colorMap, handleLegendClick, handleLegendEnter, hoveredLegend]
  );

  return (
    <WidgetContainer
      collapsible
      header={`Model Insights for ${formatModelOverviewOutcomeVariable(groupByMetricName)}`}
      subHeader='The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response.'
    >
      <div className='brand-impact-model-insights'>
        <div className='left'>
          <ChartContainer
            title={`Saturation Curves (${formatModelOverviewOutcomeVariable(groupByMetricName)})`}
            rightActions={
              <>
                <InfoTooltip size={Size.MD}>{SATURATION_INFO_COPY}</InfoTooltip>
                <DownloadDropdown onClickOptions={[excelDownloadSat, pngDownloadSat]} />
              </>
            }
          >
            {Object.keys(saturationDataNew).length !== 0 && (
              <>
                <div className='sat-wrapper'>
                  <SaturationCurveChart
                    title={formatModelOverviewOutcomeVariable(groupByMetricName)}
                    setChartRef={setChartRef}
                    xAxisHeight={xAxisHeight}
                    yAxisWidth={yAxisWidth}
                    xAxis={{
                      axisLine: false,
                      tickFormatter: val => currencyFormatter.format(val, 0),
                      domain: saturationDataXScale.domain,
                      ticks: saturationDataXScale.ticks
                    }}
                    yAxis={{
                      domain: saturationDataYScale.domain,
                      ticks: saturationDataYScale.ticks
                    }}
                    saturationData={saturationDataNew}
                    centralMeasure={centralMeasure}
                    colorMap={colorMap}
                    activeLine={activeLine}
                    hoveredLegend={hoveredLegend}
                    tooltip={tooltip}
                    showTooltip={Boolean(activeLine)}
                    xAxisLabel='Spend'
                  />
                </div>
                <div className='right-of-chart'>
                  <div className='legend'>
                    {buildLegendByGroupedArea(
                      [...saturationDataNew.groupedLines].sort(byChannelNameAsc)
                    )}
                  </div>
                  <MedianLegendLabel>
                    {centralMeasure} <br /> spend
                  </MedianLegendLabel>
                </div>
              </>
            )}
          </ChartContainer>
        </div>
        <div className='right'>
          <ChartContainer
            rightActions={
              <>
                <InfoTooltip size={Size.MD}>{EFFECT_DECAY_INFO_COPY}</InfoTooltip>
                <DownloadDropdown onClickOptions={[excelDownloadDecay, pngDownloadDecay]} />
              </>
            }
            title={`Effect Decays (${formatModelOverviewOutcomeVariable(groupByMetricName)})`}
          >
            <div className='decay-wrapper'>
              <div className='decay-chart-title'>Effect Carryover</div>
              {Object.keys(decayDataNew).length && (
                <LineChart
                  customTooltipHeader={val => `Week ${val} Effect Carryover`}
                  data={decayDataNew?.newD}
                  lineDataKeys={decayDataNew?.lines}
                  groupedAreas={[...(decayDataNew?.groupedLines ?? [])].sort(byChannelNameAsc)}
                  tooltipLabelFormatter={val => capitalizeWords(val)}
                  tooltipFormatter={val => (val ? `${numberFormatter.format(val, 0)}%` : val)}
                  usePercentageYAxis
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => `Week ${val}`}
                  xAxisLabel='Week'
                />
              )}
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
